import { IUser } from '@tacans/shared-marketplace-layer';
import React, { FC, useCallback } from 'react';
import { ExploreTopFiltersContainer } from 'src/features/common/explore/containers/explore-top-filters-container';
import { NftsExploreContainer } from 'src/features/common/explore/containers/nfts-explore-container';
import { TabItemsWrapper } from 'src/features/common/profile/components/tab-items-wrapper';
import { ExploreContentLayout } from 'src/features/white-labels/ocean/explore/components/explore-content-layout';
import { FilterButton } from 'src/features/white-labels/ocean/explore/components/filter-button';
import { CollectionsContainer } from 'src/features/white-labels/ocean/explore/containers/collections-container';
import { CollectionsSearchContainer } from 'src/features/white-labels/ocean/explore/containers/collections-search-container';
import { ExploreFilterContainer } from 'src/features/white-labels/ocean/explore/containers/explore-filter-container';
import { ProfileLaunchpadsContainer } from 'src/features/white-labels/ocean/launchpad/containers/profile-launchpads-container';
import { ProfileLaunchpadsSearchContainer } from 'src/features/white-labels/ocean/launchpad/containers/profile-launchpads-search-container';
import { ProfileDetailsContainer } from 'src/features/white-labels/ocean/profile/containers/profile-details-container';
import { EmptyListPlaceholder } from 'src/features/white-labels/ocean/shared/components/empty-list-placeholder';
import { Button } from 'src/ui/common/atoms/button';
import { Spacing } from 'src/ui/common/atoms/spacing';
import { Typography } from 'src/ui/common/atoms/typography';
import { Visible } from 'src/ui/common/layouts/visible';
import { Tabs } from 'src/ui/white-labels/ocean/molecules/tabs';

interface IProps {
    profile: IUser | null;
    selectedTabId: string;
    setSelectedTabId: (tabId: string) => void;
    isFiltersApplied: boolean;
    clearFilters: () => void;
    sideBarOpened: boolean;
    toggleSideBar: () => void;
    tabs: { tabId: string; tab: string }[];
    emptyList: { title: string; description: string; control: string };
    isProfileOwner: string | boolean | null;
    canCreate: boolean;
    handleControl: () => void;
}

export const ProfilePage: FC<IProps> = ({
    profile,
    selectedTabId,
    setSelectedTabId,
    isFiltersApplied,
    clearFilters,
    sideBarOpened,
    toggleSideBar,
    tabs,
    emptyList,
    isProfileOwner,
    canCreate,
    handleControl,
}) => {
    const emptyPlaceholder = useCallback(
        ({ itemsGridConfig }) => (
            <EmptyListPlaceholder itemsGridConfig={itemsGridConfig}>
                <Typography size="base" tag="div" variant="title" color="text">
                    {emptyList.title}
                </Typography>
                <Spacing margin="10px 0 30px">
                    <Typography size="sm" tag="div" color="gs200">
                        <span dangerouslySetInnerHTML={{ __html: emptyList.description }} />
                    </Typography>
                </Spacing>
                {(!isProfileOwner || (isProfileOwner && canCreate)) && (
                    <Button variant="white" onClick={handleControl}>
                        {emptyList.control}
                    </Button>
                )}
            </EmptyListPlaceholder>
        ),
        [canCreate, emptyList, handleControl, isProfileOwner]
    );

    return (
        <>
            <ProfileDetailsContainer profile={profile!} />
            <Tabs selectedTabId={selectedTabId} handleSelectTab={setSelectedTabId}>
                <ExploreContentLayout>
                    <ExploreContentLayout.TopPanel>
                        <Visible view={['desktop', 'laptop']}>
                            <FilterButton
                                showClearBtn={isFiltersApplied}
                                onClear={clearFilters}
                                opened={sideBarOpened}
                                onToggle={toggleSideBar}
                                disabled={selectedTabId !== 'items'}
                            />
                        </Visible>
                        <TabItemsWrapper>
                            <Tabs.TabItems tabItems={tabs} />
                        </TabItemsWrapper>
                    </ExploreContentLayout.TopPanel>
                    <ExploreContentLayout.Content>
                        <Tabs.Content tabId="items">
                            <ExploreContentLayout.SideBar opened={sideBarOpened}>
                                <ExploreFilterContainer />
                            </ExploreContentLayout.SideBar>
                            <ExploreContentLayout.ExploreContent>
                                <ExploreTopFiltersContainer />
                                <NftsExploreContainer
                                    sideBarOpened={sideBarOpened}
                                    emptyPlaceholder={emptyPlaceholder}
                                />
                            </ExploreContentLayout.ExploreContent>
                        </Tabs.Content>
                        <Tabs.Content tabId="collections">
                            <ExploreContentLayout.ExploreContent>
                                <CollectionsSearchContainer />
                                <CollectionsContainer />
                            </ExploreContentLayout.ExploreContent>
                        </Tabs.Content>
                        <Tabs.Content tabId="launchpads">
                            <ExploreContentLayout.ExploreContent>
                                <ProfileLaunchpadsSearchContainer />
                                <ProfileLaunchpadsContainer />
                            </ExploreContentLayout.ExploreContent>
                        </Tabs.Content>
                    </ExploreContentLayout.Content>
                </ExploreContentLayout>
            </Tabs>
        </>
    );
};
